/* KD9 install/update center: install the PWA, never an unverified APK.
 * Only the public offline fallback is cached by the existing service worker. */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowDownToLine, CheckCircle2, CircleHelp, ExternalLink, RefreshCw,
  ShieldCheck, Smartphone, Sparkles, Compass, ArrowRight, Share2
} from "lucide-react";
import { detectMobilePlatform, isSafariBrowser } from "./platformDetection";
import "./app-download.css";

let installPrompt = null;
// Attach when the SPA module loads (before route navigation) to retain the event.
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    installPrompt = event;
    window.dispatchEvent(new Event("kd9-install-offer"));
  });
  window.addEventListener("appinstalled", () => {
    installPrompt = null;
    window.dispatchEvent(new Event("kd9-install-offer"));
  });
}

export function isKd9InstalledPwa() {
  if (typeof window === "undefined") return false;
  return Boolean(
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator?.standalone === true
  );
}

export function shouldOpenMobileForLegacyInstall() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator?.userAgent || "";
  return isKd9InstalledPwa() && /Android|iPhone|iPad|iPod/i.test(ua);
}

/** Preserve the old "/" launch URL of PWA copies installed before the manifest changed. */
export function LegacyInstalledAppRedirect() {
  const [redirecting] = useState(shouldOpenMobileForLegacyInstall);
  useEffect(() => {
    if (redirecting && window.location.pathname === "/") {
      // Hard navigation guarantees a fresh, non-cached HTML and JS payload.
      window.location.replace("/movil");
    }
  }, [redirecting]);
  return null;
}

export default function AppDownload() {
  const [platform] = useState(() => detectMobilePlatform(navigator.userAgent, navigator.maxTouchPoints));
  const [isSafari] = useState(() => isSafariBrowser(navigator.userAgent));
  const isIOS = platform === "ios";
  const [installed, setInstalled] = useState(isKd9InstalledPwa);
  const [canInstall, setCanInstall] = useState(Boolean(installPrompt));
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");

  useEffect(() => {
    if (window.location.hash !== "#iphone") return;
    const timer = window.setTimeout(() => {
      document.getElementById("iphone")?.scrollIntoView({ block: "start" });
    }, 0);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    const sync = () => {
      setInstalled(isKd9InstalledPwa());
      setCanInstall(Boolean(installPrompt));
    };
    const query = window.matchMedia?.("(display-mode: standalone)");
    window.addEventListener("kd9-install-offer", sync);
    query?.addEventListener?.("change", sync);
    window.addEventListener("focus", sync);
    return () => {
      window.removeEventListener("kd9-install-offer", sync);
      query?.removeEventListener?.("change", sync);
      window.removeEventListener("focus", sync);
    };
  }, []);

  async function installApp() {
    if (!installPrompt || busy) return;
    setBusy(true);
    setNotice("");
    const pending = installPrompt;
    installPrompt = null;
    try {
      await pending.prompt();
      const choice = await pending.userChoice;
      if (choice?.outcome === "accepted") {
        setNotice("La instalación fue aceptada. Android completará el acceso directo a KD9.");
      } else {
        setNotice("La instalación se canceló. Puedes intentarlo desde el menú de tu navegador.");
      }
    } catch (_) {
      setNotice("Tu navegador no pudo abrir el instalador. Usa su menú «Instalar aplicación».");
    } finally {
      setCanInstall(false);
      setBusy(false);
    }
  }

  async function refreshInstalledApp() {
    if (busy) return;
    setBusy(true);
    setNotice("Comprobando que la versión más reciente esté disponible…");
    try {
      // Validate reachability first. Do not send credentials or touch private resources.
      const response = await fetch("/manifest.webmanifest", { cache: "no-store", credentials: "omit" });
      if (!response.ok) throw new Error("La red no está disponible.");
      if ("serviceWorker" in navigator) {
        const registration = await navigator.serviceWorker.getRegistration("/");
        if (registration) {
          // A transient worker update failure should not stop fetching fresh HTML.
          try { await registration.update(); } catch (_) { /* Continue with online app refresh. */ }
        }
      }
      // A real top-level request fetches the current HTML and content-hashed JS.
      window.location.replace("/movil");
    } catch (_) {
      setNotice("No fue posible comprobar la versión. Revisa la conexión e intenta nuevamente.");
      setBusy(false);
    }
  }

  return (
    <main className="kd9-download-page" data-testid="kd9-app-download">
      <div className="kd9-download-inner">
        <header className="kd9-download-top">
          <Link to="/" aria-label="Volver a KD9" className="kd9-download-wordmark">
            <img src="/logos/kd9-master.png" width="44" height="44" alt="" />
            <span>KHAOS DELTA 9</span>
          </Link>
          <Link to="/movil" className="kd9-download-open"><ExternalLink size={16}/> Abrir app</Link>
        </header>

        <section className="kd9-download-intro">
          <div className="kd9-download-emblem"><Smartphone size={42} aria-hidden="true"/><Sparkles size={20} aria-hidden="true"/></div>
          <span className="kd9-download-eyebrow">TU UNIVERSO · DESDE TU CELULAR</span>
          <h1>Descarga y actualiza KD9</h1>
          <p>Disfruta la experiencia móvil con acceso independiente, navegación inferior y las mismas funciones de tu cuenta KD9.</p>
        </section>

        <section className="kd9-download-card" aria-label="Instalación y actualización">
          <div className="kd9-download-card-title">
            <div className="kd9-download-status-icon"><CheckCircle2 size={21}/></div>
            <div><h2>{isIOS ? "KD9 para iPhone y iPad" : platform === "android" ? "KD9 para Android" : "KD9 para tu dispositivo"}</h2><span>{installed ? "Abierta como aplicación instalada" : "Versión web instalable (PWA)"}</span></div>
          </div>
          <p>Las mejoras de navegación, login móvil y acceso por roles se cargan desde nuestra plataforma en línea.</p>

          {canInstall && !installed && !isIOS && (
            <button type="button" className="kd9-download-primary" disabled={busy} onClick={installApp}
                    data-testid="kd9-install-app-button">
              <ArrowDownToLine size={20}/>{busy ? "Preparando…" : "Instalar en mi celular"}
            </button>
          )}
          <button type="button" className={canInstall && !installed && !isIOS ? "kd9-download-secondary" : "kd9-download-primary"}
                  disabled={busy} onClick={refreshInstalledApp} data-testid="kd9-update-app-button">
            <RefreshCw size={20}/>{busy ? "Comprobando…" : installed ? "Actualizar y abrir KD9" : "Abrir versión actualizada"}
          </button>
          {notice && <p className="kd9-download-notice" role="status">{notice}</p>}

          {!isIOS && !canInstall && !installed && (
            <div className="kd9-download-help">
              <CircleHelp size={19} aria-hidden="true"/>
              <p>{platform === "android"
                ? <>En Chrome para Android, abre el menú <strong>⋮</strong> y selecciona <strong>Instalar aplicación</strong> o <strong>Añadir a pantalla de inicio</strong>.</>
                : <>Abre esta página desde el navegador de tu teléfono para ver las opciones de instalación disponibles.</>}
              </p>
            </div>
          )}
        </section>

        <section id="iphone" className="kd9-download-card kd9-download-iphone-card"
                 aria-label="Descargar KD9 para iPhone" data-testid="kd9-iphone-download-card">
          <div className="kd9-download-card-title">
            <div className="kd9-download-status-icon"><Smartphone size={21} aria-hidden="true"/></div>
            <div><h2>Descargar KD9 para iPhone</h2><span>iPhone y iPad · Instalación desde Safari</span></div>
          </div>
          <p>Instala KD9 en tu pantalla de inicio y accede a la plataforma desde el icono, sin pasar por App Store.</p>
          {isIOS && installed && (
            <Link to="/movil" className="kd9-download-secondary">Abrir KD9 instalada</Link>
          )}
          <div className="kd9-download-ios-guide" data-testid="kd9-ios-install-guide">
            <div className="kd9-download-ios-title"><Share2 size={19} aria-hidden="true"/><strong>Instalar KD9 desde Safari</strong></div>
            <p>{isIOS && isSafari
              ? "Desde Safari, sigue estos pasos para agregar KD9 a tu pantalla de inicio."
              : "Desde tu iPhone o iPad, abre esta página en Safari y sigue estos pasos."}</p>
            <ol>
              <li>Abre <strong>Safari</strong> y visita <strong>khaosdelta9.com/descarga</strong>.</li>
              <li>Toca <strong>Compartir</strong> (o Menú de página → Compartir).</li>
              <li>Selecciona <strong>Agregar a Inicio</strong> y activa <strong>Abrir como app web</strong> cuando aparezca.</li>
              <li>Pulsa <strong>Agregar</strong> y abre el icono <strong>KD9</strong> desde tu pantalla de inicio.</li>
            </ol>
            <p className="kd9-download-ios-note">La instalación para iPhone es una app web (PWA); no es una descarga de App Store ni un archivo IPA.</p>
          </div>
        </section>

        <section className="kd9-download-what">
          <h2>¿Qué incluye esta versión?</h2>
          <div><Compass size={20}/><span>Navegación móvil propia con Inicio, Explorar, Mi KD9, En vivo y Perfil</span></div>
          <div><ShieldCheck size={20}/><span>Login móvil exclusivo y aviso de acceso web para Director, Administrador y Soporte</span></div>
          <div><RefreshCw size={20}/><span>Actualizaciones de contenido e interfaz al reabrir o actualizar la app</span></div>
        </section>

        <div className="kd9-download-footnote">
          <p><strong>Importante:</strong> esta instalación es una PWA. {isIOS
            ? "No equivale a una app publicada en App Store ni a instalar un archivo IPA firmado. La versión iOS de App Store y TestFlight requiere un desarrollo y revisión adicionales."
            : "No equivale a descargar un APK de Google Play. Una app Android empaquetada y firmada requiere una nueva versión para cambios de icono, permisos o funciones nativas."}</p>
          <Link to="/movil" className="kd9-download-back">Ir a KD9 Mobile <ArrowRight size={16}/></Link>
        </div>
      </div>
    </main>
  );
}
